/**
 * 五十音預生成音檔播放工具
 * 專門處理五十音相關頁面的音檔播放
 */

import { gojuonData, dakutenData } from '@/data/gojuon'
import { playAudioIOSOptimized } from './audioPlayer'
import { getDefaultTrialVoiceId } from '@/config/trial'

// 將平假名/片假名轉換為 romaji（用於音檔路徑）
// 注意：歷史假名 ゐ 和 ゑ 的 romaji 是 "wi" 和 "we"，但音檔使用 "i" 和 "e"
export const getRomajiFromKana = (kana: string): string | null => {
  // 特殊處理：歷史假名 ゐ 和 ゑ 使用 い 和 え 的音檔
  if (kana === 'ゐ' || kana === 'ヰ') {
    return 'i' // romaji 為 "wi"，但音檔使用 "i"（與 い 相同）
  }
  if (kana === 'ゑ' || kana === 'ヱ') {
    return 'e' // romaji 為 "we"，但音檔使用 "e"（與 え 相同）
  }
  
  // 合併所有五十音資料
  const allKana = [...Object.values(gojuonData).flat(), ...Object.values(dakutenData).flat()]
  
  // 查找對應的 romaji
  const found = allKana.find(item => item.hiragana === kana || item.katakana === kana)
  return found ? found.romaji : null
}

// 播放五十音預生成音檔
export const playGojuonAudio = async (
  kana: string,
  onStart?: () => void,
  onEnd?: () => void,
  onError?: (error: any) => void,
  isTrial?: boolean
): Promise<void> => {
  try {
    // 獲取 voice_id
    let voiceId: number | null = null
    
    // 試用版：直接使用試用版預設語音 ID，不調用需要身份驗證的 API
    if (isTrial) {
      voiceId = getDefaultTrialVoiceId()
    } else {
      // 正式版：使用與 playJapaneseText 相同的 API
      try {
        const response = await fetch('/api/user/voice-preference', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        })

        if (response.ok) {
          const preference = await response.json()
          voiceId = preference.voice_id
        }
      } catch (error) {
        // API 獲取失敗，使用預設值
      }
      
      // 如果失敗，使用預設 voice_id（與試用版一致，使用春日部つむぎ）
      if (!voiceId) {
        voiceId = getDefaultTrialVoiceId()  // 預設使用白石美穗 (voice_id: 8)
      }
    }
    
    // 將平假名/片假名轉換為 romaji
    const romaji = getRomajiFromKana(kana)
    if (!romaji) {
      // 降級到 VOICEVOX 即時生成
      const { playJapaneseText } = await import('@/utils/speech')
      await playJapaneseText(kana, 'gojuon', 'female')
      return
    }
    
    // 構建音檔路徑（使用 romaji）
    const audioUrl = `https://tts.jlpt-aitutor.com/audio-files/gojuon/voice_${voiceId}/${romaji}.wav`
    
    // 使用 iOS 優化的播放函數
    try {
      await playAudioIOSOptimized(audioUrl, {
        onStart: () => {
          onStart?.()
        },
        onEnd: () => {
          onEnd?.()
        },
        onError: async (error) => {
          // 降級到 VOICEVOX 即時生成
          try {
            const { playJapaneseText } = await import('@/utils/speech')
            await playJapaneseText(kana, 'gojuon', 'female')
            onEnd?.()
          } catch (err) {
            onError?.(err)
          }
        }
      })
    } catch (error) {
      // 如果播放失敗，降級到 VOICEVOX 即時生成
      try {
        const { playJapaneseText } = await import('@/utils/speech')
        await playJapaneseText(kana, 'gojuon', 'female')
        onEnd?.()
      } catch (err) {
        onError?.(err)
      }
    }
  } catch (error) {
    onError?.(error)
  }
}
